/**
 * ReviewExclusiveSubscriptionSheetContent
 *
 * Content rendered inside the ReviewExclusiveSubscriptionSheet (via
 * SheetService). This is the creator-side approve/reject surface for an
 * exclusive-content subscription request.
 *
 * Mirrors Flutter's `ReviewExclusiveSubscriptionBottomSheet`
 * (`lib/presentation/bottom_sheet/review_exclusive_subscription_bottom_sheet.dart`):
 *   - `:387-395` `appBarTitle()` — a centered "Subscription request" title
 *     (`AppStyles.bottomSheetTitleStyle`).
 *   - `:203-218` — a single centered, `TextAlign.center` explanatory paragraph
 *     (`AppStyles.bottomSheetContentStyle`).
 *   - `:266-340` `_bottomActions()` — Confirm / Reject / Close, delegated to
 *     `ReviewExclusiveSubscriptionCTAFooter`.
 *   - `:231-251` — the loading overlay rendered while `_isPerformingConfirmAction`.
 *   - `:84` — the sheet closes once `_reviewExclusiveSubscription` resolves,
 *     **unconditionally** (success and failure alike).
 *
 * ## Ownership
 *
 * This component owns every sheet invocation; the `useReviewExclusiveSubscription`
 * hook owns none (`presentation.md` §Page + feature-hook split). The hook
 * deliberately does not close the sheet, so `handleReview` closes here *after*
 * awaiting `review`, preserving Flutter's ordering: busy -> await -> un-busy ->
 * close (`:67-85`). There is no branching on the outcome and no toast — Flutter
 * discards the returned bool, and a `false` most often just means the request
 * was already cancelled server-side
 * (`cloud_functions/functions/src/functions/user_subscriptions/review_exclusive_subscription.ts:26-29`),
 * for which an error toast would be actively wrong copy.
 *
 * The title is rendered inside the content rather than passed to
 * `SheetService.show({ title })` because `.show()` is a plain module function
 * with no `useLocale()` access. This is exactly what the sibling does
 * (`SubscribeUserSheetContent.tsx:19`, `:73-83`); Task 7 passes `title: ''`.
 *
 * ## Sanctioned UI deviations (do not flag in review)
 *
 * 1. Flutter's hand-rolled sheet chrome — the `AnimatedPositioned` /
 *    `ValueNotifier` drag plumbing (`:96-186`), the `BackdropFilter` blur
 *    backdrop (`:342-358`), and the `ListView.builder`-of-one wrapper around the
 *    paragraph (`:187-220`) — is replaced by the existing `SheetService` /
 *    `ExpauseSheet` surface, which already provides slide-in, drag-to-dismiss,
 *    backdrop-tap and ESC dismissal. Reuse beats literal porting
 *    (`conventions.md` §Parity calibration).
 * 2. Flutter's app-bar close **X** (`:360-385`) has no web counterpart: the web
 *    sheet exposes dismissal via the drag handle, the backdrop, ESC and the
 *    Close CTA, so the affordance is preserved — only the idiom differs.
 * 3. `AutoSizeText`'s `minFontSize: 10` shrink-to-fit (`:213`) is not ported —
 *    the web paragraph wraps within a responsive-width sheet rather than
 *    shrinking to fit a fixed `height / 4` box.
 */
import React, { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { SheetLoadingOverlay } from '@/presentation/components/common/loadingIndicators/SheetLoadingOverlay';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useReviewExclusiveSubscription } from './hooks/useReviewExclusiveSubscription';
import { ReviewExclusiveSubscriptionCTAFooter } from './ReviewExclusiveSubscriptionCTAFooter';

export interface ReviewExclusiveSubscriptionSheetContentProps {
  /** Id of the `exclusiveSubscriptionRequested` notification being reviewed. */
  requestNotificationId: string;
  /** Id of the requesting (peer) user whose access is being reviewed. */
  userId: string;
}

export const ReviewExclusiveSubscriptionSheetContent: React.FC<
  ReviewExclusiveSubscriptionSheetContentProps
> = ({ requestNotificationId, userId }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const { isProcessing, review } = useReviewExclusiveSubscription({
    requestNotificationId,
    userId,
  });

  // Flutter :84 — close once the review resolves, unconditionally. No outcome
  // branching and no toast (see the "Ownership" note above).
  const handleReview = useCallback(
    async (accept: boolean): Promise<void> => {
      await review(accept);
      SheetService.close();
    },
    [review],
  );

  const handleConfirm = useCallback(() => {
    void handleReview(true);
  }, [handleReview]);

  const handleReject = useCallback(() => {
    void handleReview(false);
  }, [handleReview]);

  // Flutter :318 -> _closeBottomSheet() (:55-60). The busy early-return at
  // :56-58 is expressed on the web as the disabled Close button.
  const handleClose = useCallback(() => {
    SheetService.close();
  }, []);

  return (
    <div
      {...qaAttr({
        id: 'review-exclusive-subscription-sheet',
        status: isProcessing ? 'processing' : 'ready',
      })}
      className="flex flex-col"
      // Anchors the loading overlay's absolute inset-0 to this content root.
      style={{ position: 'relative' }}
    >
      <div
        className="flex flex-col"
        style={{
          padding: responsive.spacing['2xl'],
          gap: responsive.spacing.lg,
        }}
      >
        {/* Title — Flutter :387-395 (rendered here; SheetService.show has no l10n access) */}
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
            textAlign: 'center',
          }}
        >
          {l10n.reviewExclusiveSubscription.title}
        </span>

        {/* Explanatory paragraph — Flutter :203-218 */}
        <span
          style={{
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
            textAlign: 'center',
            paddingLeft: responsive.spacing['2xl'],
            paddingRight: responsive.spacing['2xl'],
          }}
        >
          {l10n.reviewExclusiveSubscription.body}
        </span>
      </div>

      {/* Confirm / Reject / Close — Flutter :266-340 */}
      <ReviewExclusiveSubscriptionCTAFooter
        isProcessing={isProcessing}
        onConfirm={handleConfirm}
        onReject={handleReject}
        onClose={handleClose}
      />

      {/* Loading overlay — Flutter :231-251 */}
      {isProcessing && (
        <SheetLoadingOverlay qaId="review-exclusive-subscription-overlay" />
      )}
    </div>
  );
};
